import {
  CLIENT_TYPES,
  CLIENT_TYPES_FOR_CARE_COORDINATOR,
} from 'client-portal/utils/prescreener/constants';
import { action, computed, get, set } from '@ember/object';
import { and, bool, equal, reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import classic from 'ember-classic-decorator';

@classic
export default class RequestInformationIndexController extends Controller {
  @service session;
  @service currentPractice;
  @service intl;
  @service request;
  @service media;
  @service pendingAppointment;
  @service prescreener;

  @tracked stripeElements = null;
  @tracked isPaymentElementLoaded = false;

  @bool('appointment.card') isCardRequired;
  @reads('model.appointment') appointment;
  @reads('appointment.client.constructor.modelName') clientModelName;
  @reads('appointment.client.isCouple') isCouple;
  @equal('clientModelName', 'client') isClientModelType;
  @and('isClientModelType', 'appointment.client.hasNoClientRelationships') isClient;
  @and('isClientModelType', 'appointment.client.hasOneClientRelationship') isContact;

  @computed('isCardRequired', 'isCardReady')
  get isReady() {
    return this.isCardRequired ? this.isCardReady : true;
  }

  @computed('isReady', 'validateFieldsTask.isRunning')
  get enableMobileSubmit() {
    return this.isReady && !this.validateFieldsTask.isRunning;
  }

  @computed('isReady', 'validateAndPersistAppointmentTask.isRunning')
  get enableDesktopSubmit() {
    return this.isReady && !this.validateAndPersistAppointmentTask.isRunning;
  }

  @computed('model.channel.careCoordinatorMode', 'usePrescreenerV2Forms')
  get subtitle() {
    if (this.usePrescreenerV2Forms) return '';

    if (this.model.channel?.careCoordinatorMode) {
      return this.intl.t('booking.information.careCoordinatorInstructions');
    }

    return this.intl.t('booking.information.thisWillOnlyBeShared');
  }

  @computed('usePrescreenerV2Forms')
  get contactTitle() {
    if (this.usePrescreenerV2Forms) {
      return this.intl.t('booking.steps.condensed.yourInfo');
    }

    return this.intl.t('booking.information.contactInformation');
  }

  @computed(
    'appointment.card.areElementsReady',
    'isPaymentElementLoaded',
    'session.canUsePaymentMethods'
  )
  get isCardReady() {
    if (this.session.canUsePaymentMethods) {
      return this.isPaymentElementLoaded;
    } else {
      return this.appointment?.card?.areElementsReady;
    }
  }

  @computed('prescreener.featurePrescreenerV2', 'prescreener.preview.isPreview')
  get usePrescreenerV2Forms() {
    return this.prescreener.featurePrescreenerV2 || this.prescreener.preview.isPreview;
  }

  get clientTypes() {
    let clientTypes = this.model.channel?.careCoordinatorMode
      ? CLIENT_TYPES_FOR_CARE_COORDINATOR
      : CLIENT_TYPES;

    return clientTypes.filter(clientType => this.allowedClientTypes.includes(clientType.type));
  }

  get allowedClientTypes() {
    return this.currentPractice.get('clientTypesAllowedToBookAppt') || ['client'];
  }

  get hasMultipleClientTypes() {
    return this.allowedClientTypes.length > 1;
  }

  get isClientTypeSelected() {
    return option => {
      const currentType = this.reservation?.clientType;
      return currentType === option.type || (!currentType && option.type === 'client');
    };
  }

  @action
  setPaymentElementLoaded(stripeElements) {
    this.stripeElements = stripeElements;
    this.isPaymentElementLoaded = true;
  }

  @action
  async onClientTypeChange(newType) {
    let card = get(this.pendingAppointment, 'appointment.card');

    this.pendingAppointment.reset();
    this.reservation.clientType = newType;

    let appointment = this.getInformationModel().appointment;

    set(appointment, 'card', card);
    set(this.model, 'appointment', appointment);

    this.prescreener.validation.resetValidation();
  }

  @action
  onResize() {
    if (!this.request) return;

    let routeName = this.request.activeRouteName;
    if (
      routeName.indexOf(`${this.request.baseRouteName}.information.`) === 0 &&
      !this.get('media.isMdDown')
    ) {
      this.request.transitionTo(this, 'information.index');
    }
  }
}
